import {lazy} from './lang.js';
import {len} from './list.js';
import {multiply} from './lazy.js';
import {tparse} from './tidal.js';
/**
 * Util functions for manipulating tidal lists
 */

/**
 * Alter nth term and return the modified list
 */

export const t_nth = (pattern, n, fn) => {
    return (...args) => {
        let _n = lazy(n, ...args);
        let _pattern = [... lazy(pattern, ...args)];
        let _fn = lazy(fn, ...args);
        let term = _fn(_pattern[_n]);
        if (Array.isArray(term)) {
            term = [... term];  
        } else {
            term = {... term};
        }  
        _pattern[_n] = term;
        return _pattern;
    };
};

export const t_operator = (operator, operand, y) => {
    return (...args) => {
        let _operand = lazy(operand, ...args);
        let _operator = lazy(operator, ...args);
        if (y) {
            let _y = {
                ... lazy(y, ...args)
            };
            _y.operator = _operator;
            _y.operand = _operand;
            return _y;
        }
        return (x) => {
            x.operator = _operator;  
            x.operand = _operand;
            return x;
        };
    };
};

// returns the nth left-most leaf from the pattern
export const t_leaf = (pattern, n) => {
    return (...args) => {
        let _pattern = lazy(pattern, ...args);
        let _n = lazy(n, ...args);    
        const leaf = (p, n, count=0) => {
            if (!Array.isArray(p)) {
                return {
                    p, count
                };
            }
            for (let term of p) {
                let ret = leaf(term, n, count);
                if (ret.count === n && ret.p) {
                    return ret;
                }
                if (Array.isArray(term)) {
                    count = ret.count;  
                } else {
                    count++; 
                }
            }
            return {
                count
            };
        };
        let flattened = _pattern.flat(16);
        return leaf(_pattern, _n % flattened.length).p;
    };
};

export const isLeaf = (x) => !Array.isArray(x);
export const isRest = (x) => x.transpose === null;
export const isAccent = (x) => x.accent;
export const isStaccato = (x) => x.staccato;

export const t_swap = (p, ... replacements) => {
    let lastPattern = undefined;
    let lazyValue = undefined;
    return (...args) => {
        let fn = (p) => {
            let _replacements = replacements.map(
                ([a,b]) => [lazy(a, ...args), lazy(b, ...args)]);

            if (isLeaf(p)) {
                let found = _replacements.find(
                    ([a,b]) => a === p.transpose);
                
                if (found) {
                    let replacer = found[1];
                    if (Array.isArray(replacer)) {
                        return replacer;
                    } else if (typeof replacer === "string") {
                        return tparse(replacer)(...args);
                    }
                    return {
                        ... p,
                        transpose: replacer
                    };
                } else {
                    return p;
                }
            }
            return p.map(
                t => fn(t));
        };
        let _p = lazy(p, ... args);
        if (_p === lastPattern) {
            return lazyValue;
        }
        lazyValue = fn(_p);
        return lazyValue;
    };
};

// replaces the given terms with a replacement
// for example t_replace(p, [0, 1], x) replaces terms 0 and 1 with x
export const t_replace = (p, terms, replacement) => {
    return (...args) => {
        let _p = lazy(p, ...args);
        let mutated = [... _p[0]];
        let _terms = lazy(terms, ...args).map(
            x => lazy(x, ...args));
        
        let _replacement = lazy(replacement, ...args);
        if (!Array.isArray(_replacement)) {
            if (_replacement === null) {
                _replacement = tparse("~")(...args);
            } else {
                _replacement = tparse(_replacement)(...args);
            }
        }
        
        for (let term of _terms) {
            mutated[term % mutated.length] = _replacement;
        }
        
        return [mutated];
    }; 
};

export const t_insert = (p, i, x) => {
    return (...args) => {
        let _p = lazy(p, ...args);
        _p = [... _p[0]];
        let _x = lazy(x, ...args);
        let _i = lazy(i, ...args);
        if (Array.isArray(_x)) {
            _p.splice(_i, 0, _x);
        } else {
            _p.splice(_i, 0, tparse(new String(_x))());
        }  
        return [_p];
    };
};

export const t_interleave = (pattern, ...terms) => {
    return (...args) => {
        let _pattern = lazy(pattern, ...args);
        _pattern = [... _pattern[0]];
        let _terms = terms.map(term => lazy(term, ...args));
        
        let len = _terms.length;
        let interleaved = _pattern.flatMap(
            (t, i) =>  
                [t, 
                 Array.isArray(_terms[i%len]) ? 
                 _terms[i%len] : 
                 tparse(new String(_terms[i%len]))()
                ]);
        return [interleaved];
    }; 
};

export const t_interleave_before = (pattern, ...terms) => {
    return (...args) => {
        let _pattern = lazy(pattern, ...args);
        _pattern = [... _pattern[0]];
        let _terms = terms.map(term => lazy(term, ...args));
        
        let len = _terms.length;
        let interleaved = _pattern.flatMap(
            (t, i) =>  
                [ 
                    Array.isArray(_terms[i%len]) ? 
                        _terms[i%len] : 
                        tparse(new String(_terms[i%len]))(),
                    t
                ]);
        return [interleaved];
    }; 
};

export const t_map = (pattern, fn) => {
    return (...args) => {
        let _pattern = lazy(pattern, ...args);
        _pattern = [... _pattern[0]];    
        return [_pattern.map((x, i) => lazy(fn(x,i), ...args))];
    };
};

export const t_append = (pattern, term, toAppend) => {
    return (...args) => {
        let _pattern = lazy(pattern, ...args);
        _pattern = [... _pattern[0]];    
        let _toAppend = lazy(toAppend, ...args);
        let _term = lazy(term, ...args) % _pattern.length;
        let t = _pattern[_term];
        if (!Array.isArray(t)) {
            t = [t];
        }
        if (!Array.isArray(_toAppend)) {
            _toAppend = tparse(new String(_toAppend))();
        }
        _pattern[_term] = [... t, _toAppend];
        return [_pattern];
    };
};

// deletes count-many terms starting at index 
export const t_splice = (pattern, index, count) => {
    return (...args) => {
        let _pattern = lazy(pattern, ...args);
        _pattern = [... _pattern[0]]; 

        let _index = lazy(index, ...args);
        let _count = lazy(count, ...args);

        _pattern.splice(_index, _count);
        return [_pattern];
    };
};

export const t_transform = (pattern, index, fn) => {
    return (...args) => {
        let _pattern = lazy(pattern, ...args);
        _pattern = [... _pattern[0]];    
        let _index = lazy(index, ...args);
        let transformed = lazy(fn(_pattern[_index]), ...args);
        _pattern[_index] = transformed;
        return [_pattern];
    }; 
};

export const t_multiply = (pattern, times) => {
    return (...args) => {
        let _pattern = lazy(pattern, ...args);
        if (!Array.isArray(_pattern)) {
            _pattern = [_pattern];
        }
        let _times = lazy(times, ...args);
        let arr = [];
        for (let i=0; i < _times; i++ ){
            arr = [... arr, ... _pattern];
        }
        return [arr];
    };
};

export const t_transform_leaf = (pattern, n, fn, skipArray=false, skipRest) => {
    return (...args) => {
        let _p = lazy(pattern, ...args);
        let og = _p
        if (!skipArray && Array.isArray(_p[0])) {
            _p = [..._p[0]];
        } else {
            _p = [... _p];
        }
        let _n = lazy(n, ...args);
        
        const run = (p, n, count=0, arr, i) => {
            if (!Array.isArray(p))  {
                if (count===n) {
                    return {
                        pattern: lazy(fn(p, arr, i), ...args),
                        count
                    };
                } else {
                    return {
                        pattern: p,
                        count
                    };
                }       
            }
            let transformed = [];
            let _i=0;
            for (let term of p) {
                let ret = run(term, n, count, p, _i++);
                transformed.push(ret.pattern);
                if (Array.isArray(term)) {
                    count = ret.count;
                } else if (skipRest && term.transpose !== null) {
                    count++;
                }
            }
            return {
                pattern: transformed,
                count
            };
        };
        
        let result = run(_p, _n);
        return skipArray ? result.pattern : [result.pattern];
    };
};

export const t_accent = (p, accent=true) => {
    return (...args) => {
        let _p = {
            ... lazy(p, ...args),
            accent: lazy(accent, ...args)
        };
        return _p;
    };
};

export const t_staccato = (p, dot=true) => {
    return (...args) => {
        let _p = {
            ... lazy(p, ...args),
            staccato: lazy(dot, ...args)
        };
        return _p;
    };
};

// replace everything but these non-rest leaves
// with rests. for example:
// t_keep_leaves(pattern, [0, 12, 16]) replaces everything
// but the 0th, 12th and 16th leaves w/ rests
export const t_keep_leaves = (p, leaves) => {
    let lazyValue, lastPattern, lastLeaves;
    return (... args) => {  
        let _p = lazy(p, ...args);
        let _leaves = lazy(leaves, ...args);

        if (lastPattern === _p && _leaves === lastLeaves) {
            return lazyValue;
        }

        lastPattern = _p;
        lastLeaves = _leaves;
        
        const run = (pattern, leaves, count=0) => {
            if (isLeaf(pattern)) {
                if (leaves.includes(count)) {
                    // we keep this leaf
                    return {
                        pattern,
                        count
                    };
                } else {
                    // otherwise we turn it into a "rest"
                    return {
                        pattern: tparse('~')(),
                        count
                    };
                }
            }
            
            let transformed = [];
            for (let term of pattern) {
                let ret = run(term, leaves, count);
                if (isLeaf(term)) {
                    count++;
                } else {
                    count = ret.count;
                }
                transformed.push(ret.pattern);        
            }
            
            return {
                count,
                pattern: transformed
            };
        };
        
        
        lazyValue = [run(_p, _leaves).pattern];
        return lazyValue;
    };
};

export const t_len = (pattern , factor=1, isTriplet) => {
    return (...args) => {
        let _factor = lazy(factor, ...args);
        let _pattern = lazy(pattern, ...args);
        let sum = 0;
        for (let i=0; i < _pattern.length; i++) {
            let term = _pattern[i];
            if (term.cycleLength) {
                sum += term.cycleLength;
            } else {
                sum++;
            }
        }
        if (lazy(isTriplet, ...args)) {
            //_factor *= window.triplet || 1;
        }
        return 2*_factor*sum;
    };
};

export const t_truncate = (pattern, param) => {
    let lastPattern, lastVal, lazyValue;
    return (...args) => {
        let val = lazy(param, ... args);
        let _pattern = lazy(pattern, ...args);
        if (val === 0) {
            return _pattern;
        }
        if (val === lastVal && lastPattern === _pattern) {
            return lazyValue;
        }

        lastVal = val;
        lastPattern = _pattern;

        // starting from end get rid of val-many figures

        let lastFigure = undefined;
        let counter=0;
        let i = _pattern.length - 1;
        for (; i >= 0; i--) {
            let term = _pattern[i];
            if (Array.isArray(term)) {
                term = term[0];
            }
            if (lastFigure !== term.figureId) {
                lastFigure = term.figureId;
                if (i !== _pattern.length - 1) {
                    counter++;
                }
                if (counter === val) {
                    break;
                }
            }
        }

        let j=0;
        for (j=0; j < _pattern.length; j++) {
            if (_pattern[j].lastFigure !== lastFigure) {
                break;
            }
        }

        let value = _pattern.slice(j, i+1);
        lazyValue = value;

        return lazyValue;
    };
};

